import * as React from "react"
import { Box, List, Stack, Typography } from "@mui/material"

import HelpRoas from "data/HelpRoas"
import HelpAttributionModels from "data/HelpAttributionModels"

const helpData: {
  [key: string]: {
    helpContent?: string | React.ReactNode
    loomEmbedUrl?: string | null
    tourName?: string
  }
} = {
  "/dashboard/companies/overview": {
    helpContent:
      "The companies tab gives you information on companies you are tracking, including a high level summary of each one.",
    loomEmbedUrl: null,
    tourName: "understandCompetitorsPortfolio",
  },
  "/dashboard/companies/search": {
    helpContent:
      "The explore page lets you filter companies in Luz's database by various criteria.",
    loomEmbedUrl: null,
  },
  "/dashboard/companies/[id]/overview": {
    helpContent:
      "The company overview page gives you high level details about a given competitor.",
    loomEmbedUrl: "https://www.loom.com/embed/3c66770fc0a0427783de2157e0ac7f9a",
  },
  "/dashboard/companies/[id]/products": {
    helpContent:
      "The company products page has details about top products that you can filter, as well as trending products, and recent product events (like product launches).",
    loomEmbedUrl: "https://www.loom.com/embed/3c66770fc0a0427783de2157e0ac7f9a",
  },
  "/dashboard/companies/[id]/marketing": {
    helpContent:
      "The company marketing page includes events like Instagram posts, homepage changes, and emails. It also includes some high level information about traffic and ad spend.",
    loomEmbedUrl: "https://www.loom.com/embed/3c66770fc0a0427783de2157e0ac7f9a",
  },
  "/dashboard/companies/[id]/reports": {
    helpContent:
      "The exports page allows you to save a downloadable file with fine-grained daily product events about a tracked competitor.",
    loomEmbedUrl: null,
  },
  "/dashboard/companies/benchmarking/companies": {
    helpContent:
      "Company benchmarking allows you to compare your own store against another competitor you are tracking.",
    loomEmbedUrl: "https://www.loom.com/embed/5000c8da655e481c89f111f826ba69bd",
  },
  "/dashboard/companies/benchmarking/products": {
    helpContent:
      "Product benchmarking allows you to compare individual products against each other, from your own company or from competitor's.",
    loomEmbedUrl: "https://www.loom.com/embed/5000c8da655e481c89f111f826ba69bd",
  },
  "/dashboard/market-insights": {
    helpContent:
      "Market Insights lets you search by term to see how product types are performing in the market.",
    loomEmbedUrl: "https://www.loom.com/embed/f770d6ec0bd043f086c380fc21f79f57",
  },
  "/dashboard/market-insights/explore": {
    helpContent:
      "Each market insight page has details about top products, and companies selling products of the given type, as well as a look at how they've performed over time.",
    loomEmbedUrl: "https://www.loom.com/embed/f770d6ec0bd043f086c380fc21f79f57",
  },
  "/dashboard/attribution/overview": {
    helpContent: (
      <>
        <Box sx={{ mb: 1 }}>
          The customers tab has details about where orders on your store
          originate.
        </Box>
        <HelpAttributionModels />
      </>
    ),
    loomEmbedUrl: "https://www.loom.com/embed/7c099dcb7c83442f86244a1f72649cc9",
    tourName: "understandAttribution",
  },
  "/dashboard/attribution/paid-ads": {
    helpContent: (
      <>
        <Box sx={{ mb: 1 }}>
          The Paid Ads section helps you attribute orders to ad campaigns you
          are running, or have run.
        </Box>
        <Typography variant="h3">Important Calculations</Typography>
        <Box sx={{ fontSize: "sm" }}>
          Luz performs a number of calculations to help you understand how your
          campaigns are performing.
        </Box>
        <HelpRoas />
      </>
    ),
    loomEmbedUrl: "https://www.loom.com/embed/7c099dcb7c83442f86244a1f72649cc9",
  },
  "/dashboard/retargeting/audiences": {
    helpContent: (
      <>
        <Box sx={{ mb: 1 }}>
          The Retargeting section utilizes the Luz pixel to identify lists of
          customers (and potential customers) that are great advertising
          opportunities.
        </Box>
        <Box sx={{ mb: 1 }}>
          The Audiences tab shows you a summation of potential opportunities the
          Segments tab as well as any audience you have already created. from
          the Segments tab as well as any audience you have already created.
        </Box>
      </>
    ),
    loomEmbedUrl: null,
  },
  "/dashboard/retargeting/segments": {
    helpContent: (
      <>
        <Box sx={{ mb: 1 }}>
          The Segments tab lists possible audiences and lists you can create for
          3rd party platforms you have integrated with.
        </Box>
        <Box sx={{ mb: 1 }}>
          Select a segment, a platform, and hit create and you will see the
          segment created as an audience in the overview tab after it is
          uploaded to the corresponding platform on your behalf.
        </Box>
      </>
    ),
    loomEmbedUrl: null,
  },
  "/dashboard/product-research": {
    helpContent: (
      <Stack spacing={1}>
        <Box>
          The Product Research page lists all of the highest selling products
          for competitors you are tracking.
        </Box>
        <Box>
          For the filters you have selected, the top 500 products are returned.
        </Box>
        <Box>
          Products can be added to Saved Collections to watch different sets of
          products or explore how they have been performing over time.
        </Box>
      </Stack>
    ),
    loomEmbedUrl: null,
  },
  "/dashboard/store/overview": {
    helpContent:
      "The Website Analytics tab has analytics about your website, and a quick look at your store's general conversion funnel comparing site visitors to sales.",
    loomEmbedUrl: null,
  },
  "/dashboard/store/pages": {
    helpContent: (
      <>
        <Box sx={{ mb: 1, fontSize: "sm" }}>
          {`Using Luz Pixel and Shopify Sales data we fit a regression line or the
          "line of best fit" through the data. This line is an estimation of the
          expected sales per page view. This helps compare products and how they
          are converting to revenue on your store.`}
        </Box>
        <Typography variant="h3">How do I use it?</Typography>
        <List sx={{ fontSize: "sm" }}>
          If a product is above the line you should consider:
          <CustomListItem text="Promoting this product in your marketing efforts" />
          <CustomListItem text="Restocking this product with new variants" />
          <CustomListItem text="Moving this product up in it's collection or on the homepage" />
          If a product is below the line you should consider:
          <CustomListItem text="Lowering the price" />
          <CustomListItem text="Changing the product photos" />
          <CustomListItem text="Not restocking this product" />
        </List>
      </>
    ),
  },
}

function CustomListItem({ text }: { text: string }) {
  return (
    <Box
      sx={{
        color: "gray.600",
        py: 0.25,
        display: "flex",
      }}
    >
      <Box
        sx={{
          backgroundColor: "primary.400",
          width: "8px",
          height: "8px",
          borderRadius: "50%",
          display: "inline-block",
          marginRight: 1,
          mt: 0.75,
        }}
      ></Box>
      {text}
    </Box>
  )
}

export { helpData }
